import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Skeleton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { MdCenterFocusStrong, MdMyLocation } from "react-icons/md";
import Map, {
  FullscreenControl,
  Marker,
  NavigationControl,
  Popup,
  ScaleControl,
  Source,
  Layer,
  MapRef,
} from "react-map-gl";
import useAxios from "../../utils/useAxios";
import { IGPSLocation, ITrackerGPSData } from "../../types/deviceInterface";
import { PinTracker } from "../map/Pin";

type TrackerRange = "24h" | "7d" | "30d";

const RANGES: { value: TrackerRange; label: string }[] = [
  { value: "24h", label: "24 h" },
  { value: "7d", label: "7 d" },
  { value: "30d", label: "30 d" },
];

const isValidLocation = (location: IGPSLocation) =>
  Number.isFinite(location.latitude) &&
  Number.isFinite(location.longitude) &&
  Math.abs(location.latitude) <= 90 &&
  Math.abs(location.longitude) <= 180;

const formatTimestamp = (timestamp: string) =>
  new Date(timestamp).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const TrackerMap = ({ deviceId }: { deviceId: string }) => {
  const api = useAxios();
  const toast = useToast();
  const mapRef = useRef<MapRef | null>(null);
  const apiRef = useRef(api);
  const toastRef = useRef(toast);
  const [range, setRange] = useState<TrackerRange>("24h");
  const [locations, setLocations] = useState<IGPSLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<IGPSLocation | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  apiRef.current = api;
  toastRef.current = toast;

  useEffect(() => {
    let isCurrent = true;

    const loadLocations = async () => {
      setIsLoading(true);
      setSelectedLocation(null);
      setSelectedIndex(null);
      try {
        const response = await apiRef.current.get<ITrackerGPSData>(
          `frontend/tracker/${deviceId}/gps-data/`,
          {
            params: { range },
          },
        );
        if (isCurrent) {
          setLocations(response.data.locations.filter(isValidLocation));
        }
      } catch {
        if (isCurrent) {
          setLocations([]);
          toastRef.current({
            title: "Unable to load GPS history",
            status: "error",
            duration: 9000,
            isClosable: true,
          });
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    loadLocations();
    return () => {
      isCurrent = false;
    };
  }, [deviceId, range]);

  const trail = useMemo(
    () => ({
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "LineString" as const,
        coordinates: locations.map((location) => [location.longitude, location.latitude]),
      },
    }),
    [locations],
  );

  const points = useMemo(
    () => ({
      type: "FeatureCollection" as const,
      features: locations.map((location, index) => ({
        type: "Feature" as const,
        properties: { index },
        geometry: {
          type: "Point" as const,
          coordinates: [location.longitude, location.latitude],
        },
      })),
    }),
    [locations],
  );

  const latestLocation = locations[locations.length - 1];

  const selectLocation = (index: number) => {
    const location = locations[index];
    if (!location) return;

    setSelectedIndex(index);
    setSelectedLocation(location);
    mapRef.current?.flyTo({
      center: [location.longitude, location.latitude],
      duration: 350,
    });
  };

  const fitTrail = () => {
    if (!mapRef.current || locations.length === 0) return;

    const longitudes = locations.map((location) => location.longitude);
    const latitudes = locations.map((location) => location.latitude);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);
    const south = Math.min(...latitudes);
    const north = Math.max(...latitudes);

    if (locations.length === 1) {
      mapRef.current.flyTo({ center: [west, south], zoom: 15, duration: 700 });
      return;
    }

    mapRef.current.fitBounds(
      [
        [west, south],
        [east, north],
      ],
      { padding: 56, duration: 700, maxZoom: 16 },
    );
  };

  const centerLatest = () => {
    if (!mapRef.current || !latestLocation) return;
    setSelectedIndex(locations.length - 1);
    setSelectedLocation(latestLocation);
    mapRef.current.flyTo({
      center: [latestLocation.longitude, latestLocation.latitude],
      zoom: 16,
      duration: 700,
    });
  };

  useEffect(() => {
    fitTrail();
  }, [locations]);

  if (isLoading) return <Skeleton height="440px" width="100%" />;

  if (locations.length === 0) {
    return (
      <VStack height="440px" justify="center" borderWidth="1px" spacing={3}>
        <Text>No GPS locations were reported in this interval.</Text>
        <ButtonGroup isAttached variant="outline" size="sm">
          {RANGES.map(({ value, label }) => (
            <Button key={value} onClick={() => setRange(value)} isActive={range === value}>
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </VStack>
    );
  }

  const timelineIndex = selectedIndex ?? locations.length - 1;
  const timelineLocation = locations[timelineIndex];

  return (
    <VStack align="stretch" spacing={3}>
      <Box height="440px" position="relative">
        <Map
          ref={mapRef}
          mapLib={import("mapbox-gl")}
          initialViewState={{
            longitude: latestLocation.longitude,
            latitude: latestLocation.latitude,
            zoom: 14,
          }}
          mapStyle="mapbox://styles/mapbox/satellite-v9"
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          interactiveLayerIds={["tracker-points-circle"]}
          onLoad={fitTrail}
          onClick={(event) => {
            const pointIndex = Number(event.features?.[0]?.properties?.index);
            if (Number.isInteger(pointIndex)) selectLocation(pointIndex);
          }}
        >
          <NavigationControl position="top-right" />
          <FullscreenControl position="top-right" />
          <ScaleControl />
          {locations.length > 1 && (
            <Source id="tracker-trail" type="geojson" data={trail}>
              <Layer
                id="tracker-trail-line"
                type="line"
                paint={{ "line-color": "#00a5a8", "line-width": 4, "line-opacity": 0.9 }}
              />
            </Source>
          )}
          <Source id="tracker-points" type="geojson" data={points}>
            <Layer
              id="tracker-points-circle"
              type="circle"
              paint={{
                "circle-radius": 4,
                "circle-color": "#ffffff",
                "circle-stroke-color": "#007a7c",
                "circle-stroke-width": 2,
              }}
            />
          </Source>
          <Marker
            latitude={latestLocation.latitude}
            longitude={latestLocation.longitude}
            anchor="bottom"
            onClick={(event) => {
              event.originalEvent.stopPropagation();
              selectLocation(locations.length - 1);
            }}
          >
            <PinTracker size={38} />
          </Marker>
          {selectedLocation && (
            <Popup
              longitude={selectedLocation.longitude}
              latitude={selectedLocation.latitude}
              anchor="bottom"
              closeOnClick={false}
              onClose={() => setSelectedLocation(null)}
            >
              <VStack align="start" spacing={1} color="black" fontSize="sm">
                <Text fontWeight="bold">{formatTimestamp(selectedLocation.timestamp)}</Text>
                {selectedLocation.accuracy !== null && (
                  <Text>Accuracy: {selectedLocation.accuracy} m</Text>
                )}
                {selectedLocation.altitude !== null && (
                  <Text>Altitude: {selectedLocation.altitude} m</Text>
                )}
              </VStack>
            </Popup>
          )}
        </Map>
        <HStack position="absolute" top={3} left={3} zIndex={1} bg="white" p={1} boxShadow="md">
          <ButtonGroup isAttached variant="outline" size="sm">
            {RANGES.map(({ value, label }) => (
              <Button key={value} onClick={() => setRange(value)} isActive={range === value}>
                {label}
              </Button>
            ))}
          </ButtonGroup>
          <Tooltip label="Fit GPS trail">
            <IconButton
              aria-label="Fit GPS trail"
              icon={<MdCenterFocusStrong />}
              size="sm"
              onClick={fitTrail}
            />
          </Tooltip>
          <Tooltip label="Center latest location">
            <IconButton
              aria-label="Center latest location"
              icon={<MdMyLocation />}
              size="sm"
              onClick={centerLatest}
            />
          </Tooltip>
        </HStack>
        <Box
          position="absolute"
          left={3}
          bottom={3}
          zIndex={1}
          maxW={{ base: "calc(100% - 24px)", md: "360px" }}
          bg="white"
          p={2}
          boxShadow="md"
        >
          <Text fontSize="sm" fontWeight="semibold">
            {locations.length} GPS positions
          </Text>
          <Text fontSize="xs">Latest: {formatTimestamp(latestLocation.timestamp)}</Text>
        </Box>
      </Box>
      <Box px={1}>
        <HStack justify="space-between" mb={1} spacing={3}>
          <Text fontSize="sm" fontWeight="semibold">
            Timeline
          </Text>
          <Text fontSize="sm" textAlign="right">
            {formatTimestamp(timelineLocation.timestamp)}
          </Text>
        </HStack>
        <Slider
          aria-label="Tracker location timeline"
          min={0}
          max={locations.length - 1}
          step={1}
          value={timelineIndex}
          onChange={selectLocation}
          colorScheme="teal"
        >
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb boxSize={5} />
        </Slider>
        <HStack justify="space-between" mt={1}>
          <Text fontSize="xs" color="gray.600">
            {formatTimestamp(locations[0].timestamp)}
          </Text>
          <Text fontSize="xs" color="gray.600">
            {formatTimestamp(latestLocation.timestamp)}
          </Text>
        </HStack>
      </Box>
    </VStack>
  );
};

export default TrackerMap;
